import { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { useMerchantId } from '@/hooks/useMerchantId';
import { useMerchant } from '@/hooks/api/merchant';
import { useCashierBranchId } from '@/hooks/api/branches';
import { useContractInvoiceGate } from '@/hooks/use-contract-invoice-gate';
import { apiClient } from '@/lib/api-client';
import { formatUZS } from '@/lib/format';
import POSLayout from '@/components/layout/POSLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { MyIdWidget } from '@/components/pos/MyIdWidget';
import { ClientManagerAlert } from '@/components/pos/ClientManagerAlert';
import { MurabahaContextBar } from '@/components/pos/MurabahaContextBar';
import { OtpPanel } from '@/components/pos/OtpPanel';
import { FlowErrorPanel } from '@/components/pos/FlowErrorPanel';
import { MurabahaDoneActions } from '@/components/pos/MurabahaDoneActions';
import { FiscalReceiptPanel } from '@/components/fiscal/FiscalReceiptPanel';
import { IncomeStep } from '@/components/pos/IncomeStep';
import { IncomeRow, defaultIncomeRow, validateIncomeRows, incomesPayload, incomeTotal } from '@/lib/income-sources';
import { requestLimit } from '@/lib/limit-check';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  UserPlus, CheckCircle, ArrowRight, Phone, ShieldCheck, ScanFace, ChevronDown, ChevronUp,
  Package, FileText, Loader2, CreditCard, UserCheck, AlertTriangle, HandCoins, Send, Clock, Lock, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

import type { paths } from '@/api/schema';

type LimitRow = paths['/customers/{customer}/limit-check']['post']['responses'][200]['content']['application/json']['limits'][number];

type CustomerType = 'new' | 'existing' | null;

type Step =
  | 'choose_type'
  | 'identify'
  | 'myid'
  | 'card_input'
  | 'customer_created'
  | 'income'
  | 'limit_result'
  | 'product_info'
  | 'contract'
  | 'contract_sms'
  | 'sent'
  | 'no_limit';


// ========== VALIDATION HELPERS ==========
const PASSPORT_REGEX = /^[A-Z]{2}\s?\d{7}$/;
const PINFL_REGEX = /^\d{14}$/;
const PHONE_REGEX = /^\+998\d{9}$/;

function validatePassportOrPinfl(value: string): { valid: boolean; type: 'passport' | 'pinfl' | null; error: string } {
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  if (PINFL_REGEX.test(cleaned)) return { valid: true, type: 'pinfl', error: '' };
  if (PASSPORT_REGEX.test(cleaned)) return { valid: true, type: 'passport', error: '' };
  if (cleaned.length === 0) return { valid: false, type: null, error: '' };
  if (/^\d+$/.test(cleaned) && cleaned.length < 14) return { valid: false, type: null, error: i18nInstance.t('pos_flow.validation.pinfl_len', { count: cleaned.length }) };
  if (/^[A-Z]{1,2}/.test(cleaned) && cleaned.length < 9) return { valid: false, type: null, error: i18nInstance.t('pos_flow.validation.passport_format') };
  return { valid: false, type: null, error: i18nInstance.t('pos_flow.validation.passport_or_pinfl') };
}

function formatCardNumber(val: string): string {
  const digits = val.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
}

function formatExpiry(val: string): string {
  const digits = val.replace(/\D/g, '').slice(0, 4);
  if (digits.length > 2) return digits.slice(0, 2) + '/' + digits.slice(2);
  return digits;
}

function validateExpiry(val: string): boolean {
  const match = val.match(/^(\d{2})\/(\d{2})$/);
  if (!match) return false;
  const month = parseInt(match[1]);
  const year = parseInt(match[2]);
  const currentYear = new Date().getFullYear() % 100;
  if (month < 1 || month > 12) return false;
  return year >= currentYear;
}


/** Bazadagi telefon (`998…`, bo'shliqli) → identify-otp talab qiladigan `+998XXXXXXXXX` */
function toE164(raw?: string | null): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('998')) return `+${digits}`;
  if (digits.length === 9) return `+998${digits}`;
  return raw ?? '';
}

function maskCardNumber(card: string): string {
  const digits = card.replace(/\s/g, '');
  if (digits.length < 8) return card;
  return digits.slice(0, 4) + ' **** **** ' + digits.slice(-4);
}

const SCHEDULE_HEAD = 3;
const SCHEDULE_TAIL = 2;

interface ScheduleRow { n: number; due_date: string; amount: number; remaining: number }

// Annuitet: summalar bir xil, shuning uchun uzun jadvalni (>6 qator) o'rtasidan yig'amiz; faqat ochish mumkin
function ScheduleTable({ schedule }: { schedule: ScheduleRow[] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const collapsed = schedule.length > SCHEDULE_HEAD + SCHEDULE_TAIL + 1 && !expanded;
  const hiddenCount = schedule.length - SCHEDULE_HEAD - SCHEDULE_TAIL;
  const isHidden = (i: number) => collapsed && i >= SCHEDULE_HEAD && i < schedule.length - SCHEDULE_TAIL;
  return (
    <table className="w-full text-sm">
      <thead><tr className="border-b border-border"><th className="text-left py-2 text-muted-foreground font-medium">№</th><th className="text-left py-2 text-muted-foreground font-medium">{t('pos_flow.term.th_date')}</th><th className="text-right py-2 text-muted-foreground font-medium">{t('pos_flow.term.th_payment')}</th><th className="text-right py-2 text-muted-foreground font-medium">{t('pos_flow.term.th_remaining')}</th></tr></thead>
      <tbody>
        {schedule.map((row, i) => (
          <Fragment key={row.n}>
            {collapsed && i === SCHEDULE_HEAD && (
              <tr className="border-b border-border">
                <td colSpan={4} className="py-2 text-center">
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); setExpanded(true); }}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    ⋯ {t('pos_flow.term.show_all', { n: hiddenCount })}
                  </button>
                </td>
              </tr>
            )}
            <tr hidden={isHidden(i)} className="border-b border-border last:border-0">
              <td className="py-2 text-muted-foreground">{row.n}</td>
              <td className="py-2">{new Date(row.due_date).toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
              <td className="py-2 text-right font-mono tabular-nums text-foreground">{formatUZS(row.amount)}</td>
              <td className="py-2 text-right font-mono tabular-nums text-muted-foreground">{formatUZS(row.remaining)}</td>
            </tr>
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

export default function MurabahaFlow() {
  const { cart, cartTotal, clearCart, murabahaCustomer } = useApp();
  // Savat tarkibi (T220): limit javobidagi muddatlar tovarlarga bog'liq, shuning uchun snapshot faqat bir xil tarkibda tiklanadi
  const basketKey = cart.map(i => `${i.product.id}x${i.quantity}`).sort().join(',');
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { merchantId } = useMerchantId();
  const navigate = useNavigate();

  // Kassirning o'zi ishlaydigan filial — E-16dan beri qo'llanilgan naqsh (Checkout.tsx bilan bir xil)
  const branchId = useCashierBranchId();

  const hasPresetCustomer = !!murabahaCustomer;
  const [customerType, setCustomerType] = useState<CustomerType>(hasPresetCustomer ? 'existing' : null);
  // Kassadan kelgan mijoz ham karta → MyID → limit tartibida davom etadi
  const [step, setStep] = useState<Step>(hasPresetCustomer ? 'card_input' : 'choose_type');

  // Identify
  const [identValue, setIdentValue] = useState('');
  const [identError, setIdentError] = useState('');
  const [phone, setPhone] = useState(hasPresetCustomer ? (murabahaCustomer!.phone || '+998') : '+998');
  const [phoneError, setPhoneError] = useState('');
  const [customerName, setCustomerName] = useState(hasPresetCustomer ? murabahaCustomer!.full_name : '');


  // Card (new customer)
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardError, setCardError] = useState('');

  // 1-qadam: tug'ilgan sana
  const [birthDate, setBirthDate] = useState('');

  // 2-qadam: MyID (WebSDK, iframe) — sessiya bekendda ochiladi (FR-91)
  const myidSessionRef = useRef<{ customerId: number; sessionId: number } | null>(null);
  const [myidMock, setMyidMock] = useState(false);
  // Connector modes come from the server (FR-40) — a hard-coded MOCK badge outlived the real integrations
  const { data: connectorsData } = useQuery({ queryKey: ['/connectors'] });
  const isMockConnector = (code: string) =>
    ((connectorsData?.data ?? []) as { code: string; mode: string }[]).some(c => c.code === code && c.mode === 'MOCK');
  // 1-qadam: yangi mijoz telefonini SMS kod bilan tasdiqlash (FR-90)
  const [identOtpSent, setIdentOtpSent] = useState(false);
  const [identOtpCode, setIdentOtpCode] = useState('');
  const [identOtpError, setIdentOtpError] = useState('');
  // FR-92: verify'dan keladigan bir martalik token — POST /customers uni ishlatadi (10 daqiqa)
  const [phoneVerificationToken, setPhoneVerificationToken] = useState<string | null>(null);
  // FR-92: joriy mijoz telefoni tasdiqlanmagan bo'lsa — SMS paneli (mijoz kartochkasida)
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtpCode, setPhoneOtpCode] = useState('');
  const [phoneOtpError, setPhoneOtpError] = useState('');

  const [myidData, setMyidData] = useState<any>(null);
  // FR-106: Client Manager did not accept the customer after MyID — shown until a retry works.
  const [clientManagerError, setClientManagerError] = useState<string | null>(null);
  // FR-106: MyID passed, Client Manager did not accept the customer — stay on the MyID step; this is where the flow goes once it does.
  const [pendingStep, setPendingStep] = useState<Step | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);



  // 3-qadam: Karta (Card Manager)
  const [cardRequestId, setCardRequestId] = useState<string | null>(null);
  // Test rejimi: server kodni hech kimga yubormaganda (MOCK) uni otp_hint sifatida qaytaradi
  const [identOtpHint, setIdentOtpHint] = useState<string | null>(null);
  const [phoneOtpHint, setPhoneOtpHint] = useState<string | null>(null);
  const [cardOtpHint, setCardOtpHint] = useState<string | null>(null);
  // Kassadan kelgan mijozning telefoni tasdiqlanmagan — stepperda «Mijoz» qadami ko'rinadi
  const [presetPhoneStep, setPresetPhoneStep] = useState(false);
  const [cardOtpSent, setCardOtpSent] = useState(false);
  const [cardExpiresAt, setCardExpiresAt] = useState<string | null>(null);
  const [cardMasked, setCardMasked] = useState<string>('');
  // T222: the person's cards this merchant may use (CustomerResource.cards); the chosen one goes onto the case
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [addingCard, setAddingCard] = useState(false);
  // the bank sends its OTP once per card — for a card we already hold the code is ours, to the phone it is linked to
  const [cardOwnCode, setCardOwnCode] = useState(false);
  // Bank sends the SMS from the card processor — show the phone it went to, as the bank masks it
  const [cardPhoneMask, setCardPhoneMask] = useState<string | null>(null);

  // 5-qadam (Limitdan oldin): Daromad manbalari
  const [incomeRows, setIncomeRows] = useState<IncomeRow[]>([defaultIncomeRow()]);
  const [incomeError, setIncomeError] = useState('');

  // 6-qadam: Hisobga sotuv javobi
  const [saleResult, setSaleResult] = useState<any>(null);
  const [itemsOpen, setItemsOpen] = useState(true);

  // Xatolar / tiklanish
  const [stepError, setStepError] = useState<string | null>(null);
  const [limitLoading, setLimitLoading] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);




  // SMS
  const [smsCode, setSmsCode] = useState('');
  const [smsError, setSmsError] = useState('');
  const [contractSmsCode, setContractSmsCode] = useState('');
  const [contractSmsError, setContractSmsError] = useState('');

  // State
  const [loading, setLoading] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [contractSmsSent, setContractSmsSent] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<number | null>(null);

  // Backend-returned data
  const [customerId, setCustomerId] = useState<string | null>(hasPresetCustomer ? murabahaCustomer!.id : null);
  const [limitData, setLimitData] = useState<any>(null);
  const [existingCustomer, setExistingCustomer] = useState<any>(hasPresetCustomer ? murabahaCustomer : null);
  const [lookupNotFound, setLookupNotFound] = useState(false);
  // FR-92: joriy mijoz (qidiruv / kassa) — bekend telefoni tasdiqlanganini bilmaguncha karta/MyID bloklanadi
  const phoneUnverified = customerType === 'existing' && !!existingCustomer && !existingCustomer.phone_verified_at;

  // Invoice (6-qadam) uchun rekvizitlar
  const { data: merchant } = useMerchant();
  const { data: hisobRequisites } = useQuery({
    queryKey: ['/platform-settings/hisob_requisites'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/platform-settings/{key}', {
        params: { path: { key: 'hisob_requisites' } },
      });
      if (error) throw error;
      return (data?.data?.value ?? {}) as any;
    },
  });

  const [murabahaCaseId, setMurabahaCaseId] = useState<string | null>(null);

  // T231: with `loan_settings.contract_waits_invoice` on, the contract (step 8) waits for the approved e-invoice (step 7)
  const { waiting: waitingInvoice, invoice: caseInvoice } = useContractInvoiceGate(murabahaCaseId, step === 'product_info');
  const [contractId, setContractId] = useState<string | null>(null);
  const [contractNumber, setContractNumber] = useState<string>('');

  // FR-93: ilova orqali tasdiq — server MOCK rejimda 8 s dan keyin o'zi imzolaydi
  const [confirmationMethod, setConfirmationMethod] = useState<'app' | 'sms' | null>(null);
  const [contractConfirmed, setContractConfirmed] = useState(false);
  const [cancelContractOpen, setCancelContractOpen] = useState(false);
  const [appTimedOut, setAppTimedOut] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopPolling = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = null;
  };
  useEffect(() => stopPolling, []);

  // ===== Sessiyani saqlash / tiklash (maxfiy ma'lumotlar saqlanmaydi) =====
  const sessionKey = `murabaha_flow:${merchantId || 'x'}:${user?.id || 'x'}`;
  const [resumeSnapshot, setResumeSnapshot] = useState<any>(null);
  const restoredRef = useRef(false);

  useEffect(() => {
    if (restoredRef.current || !merchantId) return;
    restoredRef.current = true;
    try {
      const raw = sessionStorage.getItem(sessionKey);
      if (!raw) return;
      const snap = JSON.parse(raw);
      const sameCustomer = !hasPresetCustomer || snap?.customerId === murabahaCustomer?.id;
      const meaningful = snap?.step && snap.step !== 'choose_type' && (!hasPresetCustomer || snap.step !== 'card_input');

      if (snap?.cartTotal === cartTotal && snap?.basketKey === basketKey && meaningful && sameCustomer) {
        setResumeSnapshot(snap);
      } else {
        sessionStorage.removeItem(sessionKey);
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, sessionKey]);

  const applySnapshot = (snap: any) => {
    setCustomerType(snap.customerType ?? null);
    setIdentValue(snap.identValue ?? '');
    setBirthDate(snap.birthDate ?? '');
    setPhone(snap.phone ?? '+998');
    setCustomerName(snap.customerName ?? '');
    setCustomerId(snap.customerId ?? null);
    setClientId(snap.clientId ?? null);
    setCardMasked(snap.cardMasked ?? '');
    setSelectedCardId(snap.selectedCardId ?? null);
    // T219: eski ko'rinishdagi (xarita) snapshot yaroqsiz — limit qayta so'raladi
    setLimitData(Array.isArray(snap.limitData?.limits) ? snap.limitData : null);
    setSelectedTerm(snap.selectedTerm ?? null);
    setExistingCustomer(snap.existingCustomer ?? null);
    // a snapshot with only blank rows (saved before the pre-filled row) must not wipe the default one
    if (Array.isArray(snap.incomeRows) && snap.incomeRows.some((r: IncomeRow) => r.source_code || r.monthly_amount)) setIncomeRows(snap.incomeRows);
    // SMS tokeni saqlanmaydi (FR-92): mijoz hali yaratilmagan bo'lsa, telefon qaytadan tasdiqlanadi
    setStep(snap.step === 'myid' && !snap.customerId ? 'identify' : snap.step);
    setResumeSnapshot(null);
  };

  const discardSnapshot = () => {
    sessionStorage.removeItem(sessionKey);
    setResumeSnapshot(null);
  };

  useEffect(() => {
    if (!merchantId || resumeSnapshot) return;
    if (step === 'choose_type' || step === 'sent') {
      sessionStorage.removeItem(sessionKey);
      return;
    }
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify({
        step, customerType, identValue, birthDate, phone, customerName,
        customerId, clientId, cardMasked, selectedCardId, limitData, selectedTerm, cartTotal, basketKey,
        existingCustomer, incomeRows,
      }));
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, customerType, identValue, birthDate, phone, customerName, customerId, clientId, cardMasked, selectedCardId, limitData, selectedTerm, cartTotal, basketKey, merchantId, existingCustomer, incomeRows]);

  // FR-92: kassadan kelgan mijozning telefoni tasdiqlanganmi — bekenddan so'raladi
  useEffect(() => {
    if (!hasPresetCustomer) return;
    let cancelled = false;
    void apiClient.GET('/customers/{customer}', { params: { path: { customer: Number(murabahaCustomer!.id) } } }).then(({ data }) => {
      if (cancelled || !data?.data) return;
      const fresh = data.data;
      setExistingCustomer((prev) => ({ ...(prev || {}), ...fresh }));
      if (!fresh.phone_verified_at) {
        setPhone(toE164(fresh.phone));
        setPresetPhoneStep(true);
        setStep((s) => (s === 'card_input' ? 'customer_created' : s));
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPresetCustomer]);

  // Pre-set limits from murabahaCustomer context
  const presetLimit = hasPresetCustomer ? murabahaCustomer!.murabaha_limit : null;

  // Muddatlar, ustama, jami, oylik to'lov, grafik va limit — hammasi backend javobidan (T219); mijozda hisob-kitob yo'q
  const termOptions = ((limitData?.limits ?? []) as LimitRow[]).map(l => ({
    tenor: l.tenor_months,
    markup: l.markup_percent / 100,
    markupAmount: l.markup_amount,
    limit: l.limit,
    total: l.total_amount,
    monthly: l.monthly_payment,
    schedule: l.schedule,
    canAfford: l.affordable,
  }));

  const affordableTerms = termOptions.filter(o => o.canAfford);
  const hasLimit = affordableTerms.length > 0;
  const selectedInfo = termOptions.find(o => o.tenor === selectedTerm) ?? termOptions[0];
  const maxLimit = termOptions.length ? Math.max(0, ...termOptions.map(o => o.limit)) : 0;


  // ========== HANDLERS ==========
  const handleChooseType = (type: CustomerType) => {
    setCustomerType(type);
    setLookupNotFound(false);
    setStep('identify');
  };

  /** Daromadlarni yozib, so'ng Loan Managerdan limit so'raydi; natijaga qarab qadamni tanlaydi */
  const runLimitCheck = async (id: string) => {
    setLimitLoading(true);
    setStepError(null);
    try {
      const result = await postIncomesAndCheckLimit(id);
      setLimitData(result);
      setStep(result.approved ? 'limit_result' : 'no_limit');
    } catch (err: any) {
      setStepError(err.message || t('pos_flow.errors.limit_check'));
    } finally {
      setLimitLoading(false);
    }
  };

  /** Daromadlar limit so'rovi bilan birga ketadi (Lovable check_limit): server ularni saqlangan daromadlar o'rniga yozadi — qayta so'rash limitni oshirmaydi */
  const postIncomesAndCheckLimit = async (id: string) => {
    // T224: the bank scores asynchronously — requestLimit polls our status until it is final (rejection is an answer, a failure throws)
    const data = await requestLimit(Number(id), {
      basket_amount: cartTotal,
      incomes: incomesPayload(incomeRows),
      product_ids: cart.map(i => Number(i.product.id)),
    });

    return {
      limits: data.limits,
      expire_date: data.expires_at ?? null,
      approved: data.approved,
      status: data.status,
      error_code: data.error_code,
      violation_codes: data.violation_codes,
    };
  };

  /** Client Manager mijozni qaytargandan keyin — mijoz ma'lumoti qadami */
  const proceedExisting = async (c: any) => {
    setExistingCustomer(c);
    setCustomerId(String(c.id));
    if (c.client_id) setClientId(c.client_id);
    if (c.full_name) setCustomerName(c.full_name);
    if (c.phone) setPhone(toE164(c.phone));
    resetPhoneOtp();
    setStep('customer_created');
  };


  /** Joriy mijozni /customers/lookup orqali qidirish (passport yoki PINFL) */
  const lookupExistingCustomer = async () => {
    const validation = validatePassportOrPinfl(identValue);
    const passportValue = identValue.replace(/\s/g, '').toUpperCase();
    setLoading(true);
    setStepError(null);
    setLookupNotFound(false);
    try {
      const { data, error } = await apiClient.GET('/customers/lookup', {
        params: {
          // Lovable customer_lookup kabi — hujjat + kassir kiritgan telefon
          query: (validation.type === 'passport'
            ? { passport: passportValue, phone }
            : { pinfl: passportValue, phone }) as never,
        },
      });
      if (error) throw error;

      if (data?.data) {
        await proceedExisting(data.data);
      } else {
        setLookupNotFound(true);
      }
    } catch (err: any) {
      setStepError(err.message || t('pos_flow.errors.generic_network'));
    } finally {
      setLoading(false);
    }
  };

  /** Topilmagan mijozni yangi mijoz sifatida davom ettirish */
  const switchToNewCustomer = () => {
    setLookupNotFound(false);
    setStepError(null);
    setCustomerType('new');
    setIdentOtpSent(false);
    setIdentOtpCode('');
    setStep('identify');
  };

  /** Boshqa mijozni tekshirish — maydonlarni tozalash */
  const resetIdentify = () => {
    setLookupNotFound(false);
    setStepError(null);
    setIdentValue('');
    setPhone('+998');
    setBirthDate('');
    setCustomerId(null);
    setCustomerName('');
    setExistingCustomer(null);
    setLimitData(null);
    setStep('identify');
  };

  // ===== 1-QADAM: Hujjat + telefon (+ yangi mijoz uchun tug'ilgan sana) =====
  const handleIdentSubmit = async () => {
    const validation = validatePassportOrPinfl(identValue);
    if (!validation.valid) {
      setIdentError(validation.error || t('pos_flow.validation.enter_passport_or_pinfl'));
      return;
    }
    if (!PHONE_REGEX.test(phone)) {
      setPhoneError(t('pos_flow.validation.phone_format'));
      return;
    }
    setIdentError('');
    setPhoneError('');

    if (customerType === 'new' && !/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
      setIdentError(t('pos_flow.validation.birth_date'));
      return;
    }

    // Joriy mijoz — to'g'ridan-to'g'ri Client Managerdan qidiriladi
    if (customerType === 'existing') {
      await lookupExistingCustomer();
      return;
    }

    // Yangi mijoz — F.I.Sh. so'ralmaydi: ism MyID dan keladi (Lovable bilan bir xil)
    await sendIdentOtp(false);
  };

  const sendIdentOtp = async (resend: boolean) => {
    setLoading(true);
    setIdentOtpError('');
    try {
      const { data, error, response } = await apiClient.POST('/murabaha/identify-otp', { body: { phone } });
      if (error) {
        const message = response.status === 429 ? t('murabaha_flow.identify_otp.rate_limited') : t('murabaha_flow.identify_otp.send_error');
        if (resend) setIdentOtpError(message);
        else toast.error(message);
        return;
      }
      setIdentOtpSent(true);
      setIdentOtpHint(data?.otp_hint ?? null);
      setIdentOtpCode('');
      toast.success(t(resend ? 'murabaha_flow.identify_otp.resent' : 'murabaha_flow.identify_otp.sent'));
    } finally {
      setLoading(false);
    }
  };

  const handleIdentOtpVerify = async () => {
    if (identOtpCode.length !== 6) {
      setIdentOtpError(t('murabaha_flow.identify_otp.code_length'));
      return;
    }
    setLoading(true);
    setIdentOtpError('');
    try {
      const { data, error, response } = await apiClient.POST('/murabaha/identify-otp/verify', { body: { phone, code: identOtpCode } });
      if (error || !data) {
        setIdentOtpError(t(response.status === 429 ? 'murabaha_flow.identify_otp.rate_limited' : 'murabaha_flow.identify_otp.invalid_code'));
        return;
      }
      setPhoneVerificationToken(data.verification_token);
      setIdentOtpSent(false);
      setIdentOtpCode('');
      setStep('myid');
    } finally {
      setLoading(false);
    }
  };

  const resetIdentOtp = () => { setIdentOtpSent(false); setIdentOtpCode(''); setIdentOtpError(''); };

  // ===== FR-92: joriy mijoz telefonini tasdiqlash (bazada phone_verified_at yo'q) =====
  const resetPhoneOtp = () => { setPhoneOtpSent(false); setPhoneOtpCode(''); setPhoneOtpError(''); };

  const sendPhoneOtp = async (resend: boolean) => {
    setLoading(true);
    setPhoneOtpError('');
    try {
      const { data, error, response } = await apiClient.POST('/murabaha/identify-otp', { body: { phone } });
      if (error) {
        setPhoneOtpError(t(response.status === 429 ? 'murabaha_flow.identify_otp.rate_limited' : 'murabaha_flow.identify_otp.send_error'));
        return;
      }
      setPhoneOtpSent(true);
      setPhoneOtpHint(data?.otp_hint ?? null);
      setPhoneOtpCode('');
      toast.success(t(resend ? 'murabaha_flow.identify_otp.resent' : 'murabaha_flow.identify_otp.sent'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneOtpVerify = async () => {
    if (phoneOtpCode.length !== 6) {
      setPhoneOtpError(t('murabaha_flow.identify_otp.code_length'));
      return;
    }
    if (!customerId) return;
    setLoading(true);
    setPhoneOtpError('');
    try {
      const verified = await apiClient.POST('/murabaha/identify-otp/verify', { body: { phone, code: phoneOtpCode } });
      if (verified.error || !verified.data) {
        setPhoneOtpError(t(verified.response.status === 429 ? 'murabaha_flow.identify_otp.rate_limited' : 'murabaha_flow.identify_otp.invalid_code'));
        return;
      }
      const { data, error } = await apiClient.POST('/customers/{customer}/phone-verification', {
        params: { path: { customer: Number(customerId) } },
        body: { phone_verification_token: verified.data.verification_token },
      });
      if (error || !data) {
        setPhoneOtpError(t('murabaha_flow.phone_verification.failed'));
        return;
      }
      setExistingCustomer((prev) => ({ ...(prev || {}), ...data.data }));
      resetPhoneOtp();
      toast.success(t('murabaha_flow.phone_verification.done'));
    } finally {
      setLoading(false);
    }
  };

  // ===== 2-QADAM: MyID identifikatsiya =====

  const myidFailureMessage = (note?: string | null, code?: number | null) => {
    switch (note) {
      case 'profile_mismatch':
        return t('murabaha_flow.myid.profile_mismatch');
      case 'document_expired':
        return t('murabaha_flow.myid.document_expired');
      case 'profile_unavailable':
        return t('murabaha_flow.myid.profile_unavailable');
      default:
        return t('murabaha_flow.myid.failed', { note: note ?? '', code: code ?? '—' });
    }
  };

  // MyID sessiyasini ochadi: yangi mijoz avval POST /customers orqali yaratiladi
  const handleMyIdStart = async (): Promise<{ sdkUrl: string | null } | null> => {
    const typed = identValue.replace(/\s/g, '').toUpperCase();
    const pinfl = identValidation.type === 'pinfl' && typed ? typed : (existingCustomer?.pinfl ?? '');
    const passport = identValidation.type === 'passport' && typed ? typed : (existingCustomer?.passport ?? '');
    const birth = birthDate || existingCustomer?.birth_date || '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birth)) {
      setStepError(t('murabaha_flow.myid.birth_date_required'));
      return null;
    }
    setStepError(null);
    // FR-92: token muddati o'tgan / ishlatilgan — telefonni qaytadan SMS bilan tasdiqlash kerak
    const tokenRejected = (error: unknown) => {
      if (!(error as { errors?: Record<string, unknown> } | undefined)?.errors?.phone_verification_token) return false;
      setPhoneVerificationToken(null);
      resetIdentOtp();
      setStep('identify');
      toast.error(t('murabaha_flow.phone_verification.expired'));
      return true;
    };
    try {
      let targetCustomerId = customerId;
      let pendingToken = phoneVerificationToken;
      if (customerType !== 'existing' && !targetCustomerId) {
        const { data, error } = await apiClient.POST('/customers', {
          body: {
            phone,
            source: 'murabaha_application',
            passport: passport || undefined,
            pinfl: pinfl || undefined,
            phone_verification_token: phoneVerificationToken ?? undefined,
          },
        });
        if (tokenRejected(error)) return null;
        if (error) throw error;
        targetCustomerId = String(data!.data.id);
        setCustomerId(targetCustomerId);
        setPhoneVerificationToken(null);
        pendingToken = null;
      } else if (customerType !== 'existing' && targetCustomerId) {
        // Yangi mijoz oldin yaratilgan, kassir orqaga qaytib hujjat/telefonni tuzatgan bo'lishi mumkin — lidga yoziladi
        const { error } = await apiClient.PATCH('/customers/{customer}', {
          params: { path: { customer: Number(targetCustomerId) } },
          body: { phone, passport: passport || null, pinfl: pinfl || null },
        });
        if (error) throw error;
      }
      if (targetCustomerId && pendingToken) {
        // FR-92: mijoz avval yaratilgan (tiklangan sessiya / identify qadamiga qaytish) — yangi SMS tokeni shu mijozga yoziladi
        const { data, error } = await apiClient.POST('/customers/{customer}/phone-verification', {
          params: { path: { customer: Number(targetCustomerId) } },
          body: { phone_verification_token: pendingToken },
        });
        if (tokenRejected(error)) return null;
        if (error) throw error;
        setExistingCustomer((prev) => ({ ...(prev || {}), ...data!.data }));
        setPhoneVerificationToken(null);
      }
      if (!targetCustomerId) {
        setStepError(t('murabaha_flow.myid.no_customer'));
        return null;
      }

      const lang = (['uz', 'ru', 'en'] as const).find((l) => i18n.language?.startsWith(l)) ?? 'uz';
      const { data, error, response } = await apiClient.POST('/customers/{customer}/myid-sessions', {
        params: { path: { customer: Number(targetCustomerId) } },
        body: {
          pinfl: pinfl || undefined,
          passport: pinfl ? undefined : passport || undefined,
          birth_date: birth,
          lang,
        },
      });
      if (error || !data) {
        setStepError(response.status === 502 ? t('murabaha_flow.myid.unavailable') : (error as { message?: string })?.message || t('murabaha_flow.myid.start_error'));
        return null;
      }
      myidSessionRef.current = { customerId: Number(targetCustomerId), sessionId: data.data.id };
      setMyidMock(data.data.sdk_url === null);
      return { sdkUrl: data.data.sdk_url };
    } catch (err) {
      setStepError((err as { message?: string })?.message || t('murabaha_flow.myid.start_error'));
      return null;
    }
  };

  // Iframe LIVENESS_PASSED dedi — natijani bekend MyID dan o'zi oladi
  const handleMyIdPassed = async () => {
    const session = myidSessionRef.current;
    if (!session) return;
    const isExisting = customerType === 'existing';
    setLoading(true);
    setStepError(null);
    setPendingStep(null);
    try {
      const { data, error, response } = await apiClient.POST('/customers/{customer}/myid-sessions/{myidSession}/complete', {
        params: { path: { customer: session.customerId, myidSession: session.sessionId } },
      });
      if (error || !data?.data.customer) {
        myidSessionRef.current = null;
        const failed = (error as { data?: { result_note?: string | null; result_code?: number | null } } | undefined)?.data;
        setStepError(response.status === 502 ? t('murabaha_flow.myid.unavailable') : myidFailureMessage(failed?.result_note, failed?.result_code));
        return;
      }

      const customer = data.data.customer;
      setMyidData({ comparison_value: customer.myid_comparison_value ? Number(customer.myid_comparison_value) : null });
      setClientId(customer.client_id ?? null);
      setClientManagerError(customer.client_manager_synced ? null : (customer.client_manager_error ?? 'unknown'));
      if (customer.full_name) setCustomerName(customer.full_name);
      toast.success(isExisting ? t('murabaha_flow.myid.success_existing') : t('murabaha_flow.myid.success_new'));

      if (isExisting) setExistingCustomer({ ...(existingCustomer || {}), ...customer });
      const next: Step = isExisting ? 'income' : 'card_input';
      // A contract cannot open without `client_id`, so a card or income step now would be wasted.
      if (customer.client_manager_synced) setStep(next);
      else setPendingStep(next);
    } catch (err) {
      setStepError((err as { message?: string })?.message || t('murabaha_flow.myid.failed', { note: '', code: '—' }));
    } finally {
      setLoading(false);
    }
  };

  // FR-106: repeat the Client Manager call from the stored MyID profile
  const handleClientManagerRetry = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const { data, error, response } = await apiClient.POST('/customers/{customer}/client-manager/sync', {
        params: { path: { customer: Number(customerId) } },
      });
      if (error || !data) {
        setStepError(response.status === 422 ? t('murabaha_flow.client_manager.no_profile') : t('murabaha_flow.client_manager.failed'));
        return;
      }
      if (data.data.client_manager_synced) {
        setClientManagerError(null);
        toast.success(t('murabaha_flow.client_manager.success'));
        if (pendingStep) {
          setStep(pendingStep);
          setPendingStep(null);
        }
      } else {
        setClientManagerError(data.data.client_manager_error ?? 'unknown');
      }
    } catch {
      setStepError(t('murabaha_flow.client_manager.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleMyIdExit = () => {
    const session = myidSessionRef.current;
    myidSessionRef.current = null;
    if (!session) return;
    void apiClient.POST('/customers/{customer}/myid-sessions/{myidSession}/close', {
      params: { path: { customer: session.customerId, myidSession: session.sessionId } },
    });
  };


  // ===== 3-QADAM: Karta (Card Manager + OTP) =====
  const handleCardSubmit = async () => {
    const digits = cardNumber.replace(/\s/g, '');
    if (digits.length !== 16) {
      setCardError(t('pos_flow.errors.card_16'));
      return;
    }
    if (!validateExpiry(cardExpiry)) {
      setCardError(t('pos_flow.errors.card_expiry'));
      return;
    }

    if (!customerId) {
      setCardError(t('pos_flow.errors.customer_unknown'));
      return;
    }

    setCardError('');
    setLoading(true);
    try {
      const { data, error } = await apiClient.POST('/customers/{customer}/card', {
        params: { path: { customer: Number(customerId) } },
        body: { card_number: digits, card_expiry: cardExpiry },
      });
      if (error) throw error;
      if (data.source === 'linked' && data.card) {
        // the merchant already has this card: nothing to confirm
        const linked = data.card;
        setExistingCustomer((prev: any) => ({ ...(prev || {}), cards: [linked, ...((prev?.cards ?? []).filter((c: any) => c.id !== linked.id))], has_card: true }));
        setSelectedCardId(linked.id);
        setCardMasked(linked.masked);
        proceedAfterCard();
        return;
      }
      setCardOwnCode(data.source === 'own');
      setCardRequestId(data.request_id ?? null);
      setCardExpiresAt(data.expires_at ?? null);
      setCardMasked(data.card_masked ?? maskCardNumber(cardNumber));
      setCardOtpHint(data.otp_hint ?? null);
      setCardPhoneMask(data.phone_mask ?? null);
      setCardOtpSent(true);
      toast.success(t('pos_flow.card_code_sent'));
    } catch (err: any) {
      setCardError(err.message || t('pos_flow.errors.card_add'));
    } finally {
      setLoading(false);
    }
  };

  const proceedAfterCard = () => {
    setAddingCard(false);
    setStep(customerType === 'existing' && customerId ? 'myid' : 'customer_created');
  };

  // Yangi SMS shu karta uchun (bank yangi verifyId beradi) — kartani qayta kiritish shart emas
  const handleCardResend = async () => {
    if (!customerId || !cardRequestId) {
      setSmsError(t('pos_flow.errors.request_unknown'));
      return;
    }
    setSmsError('');
    setLoading(true);
    try {
      const { data, error, response } = await apiClient.POST('/customers/{customer}/card/resend', {
        params: { path: { customer: Number(customerId) } },
        body: { request_id: cardRequestId },
      });
      if (response.status === 410) {
        resetCardOtp();
        setCardError(t('otp.card_link_expired'));
        return;
      }
      if (error) throw error;
      setCardExpiresAt(data.expires_at);
      if (data.otp_hint) setCardOtpHint(data.otp_hint);
      toast.success(t('pos_flow.card_code_sent'));
    } catch (err: any) {
      setSmsError(err.message || t('pos_flow.errors.card_add'));
    } finally {
      setLoading(false);
    }
  };

  const resetCardOtp = () => {
    setCardPhoneMask(null);
    setCardOtpSent(false);
    setCardRequestId(null);
    setCardExpiresAt(null);
    setCardOwnCode(false);
    setSmsCode('');
    setSmsError('');
  };

  const handleCardOtpVerify = async () => {
    if (smsCode.length !== 6) {
      setSmsError(t('pos_flow.errors.sms_6'));
      return;
    }
    if (!customerId || !cardRequestId) {
      setSmsError(t('pos_flow.errors.request_unknown'));
      return;
    }
    setSmsError('');
    setLoading(true);
    try {
      const { data, error, response } = await apiClient.POST('/customers/{customer}/card/confirm', {
        params: { path: { customer: Number(customerId) } },
        body: { code: smsCode, request_id: cardRequestId },
      });
      // 410: so'rov (va OTP) muddati tugagan — kartani qayta qo'shish kerak
      if (response.status === 410) {
        resetCardOtp();
        setCardError(t('otp.card_link_expired'));
        return;
      }
      if (error) throw error;
      const newest = data.data.cards?.[0];
      setCardMasked(newest?.masked ?? maskCardNumber(cardNumber));
      setSelectedCardId(newest?.id ?? null);
      setExistingCustomer((prev: any) => ({ ...(prev || {}), ...data.data }));
      proceedAfterCard();

    } catch (err: any) {
      setSmsError(err.message || t('pos_flow.errors.confirm'));
    } finally {
      setLoading(false);
    }
  };

  // ===== DAROMAD QADAMI → Loan Manager limit so'rovi =====
  const handleSubmitIncome = async () => {
    const check = validateIncomeRows(incomeRows);
    if (!check.valid) {
      setIncomeError(check.error);
      return;
    }
    setIncomeError('');
    if (!customerId) {
      setStepError(t('pos_flow.errors.customer_unknown'));
      return;
    }
    await runLimitCheck(customerId);
  };


  // Mavjud mijoz kassadan to'g'ridan-to'g'ri kelganda limitni Loan Managerdan olamiz
  const fetchPresetLimit = async () => {
    if (!customerId) return;
    setLimitLoading(true);
    setStepError(null);
    try {
      const result = await postIncomesAndCheckLimit(customerId);
      setLimitData(result);
    } catch (err: any) {
      setStepError(err.message || t('pos_flow.errors.limit_get'));
    } finally {
      setLimitLoading(false);
    }
  };

  useEffect(() => {
    // Preset mijoz uchun limit faqat karta + MyID bajarilgandan keyin (limit qadamida) olinadi
    if (!hasPresetCustomer || limitData || !customerId || step !== 'limit_result') return;
    fetchPresetLimit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPresetCustomer, customerId, merchantId, step]);


  const handleSelectTerm = (term: number) => {
    setSelectedTerm(term);
    setStep('product_info');
  };

  // Tovarning to'liq (kanonik) ma'lumotlari — Hisobga shu holatda yuboriladi
  const invoiceItems = cart.map(item => ({
    product_id: item.product.id,
    product_name: item.product.name,
    sku: item.product.sku,
    barcode: item.product.barcode || '',
    brend_nomi: (item.product as any).brend_nomi || '',
    atribut_nomi: (item.product as any).atribut_nomi || '',
    mhik: item.product.mhik || '',
    mxik_nomi: (item.product as any).mxik_nomi || '',
    category: item.product.category || '',
    unit: item.selected_unit || item.product.unit || 'dona',
    is_murabaha_eligible: item.product.is_murabaha_eligible,
    quantity: item.quantity,
    unit_price: item.unit_price,
    total: item.total,
  }));

  // ===== 6-QADAM: "Tovarni sotish" (POST /murabaha/cases) =====
  const handleSellGoods = async () => {
    if (!customerId || !branchId || !selectedTerm) {
      setStepError(t('pos_flow.errors.sale_ids'));
      return;
    }
    setLoading(true);
    setStepError(null);
    try {
      const { data, error } = await apiClient.POST('/murabaha/cases', {
        body: {
          customer_id: Number(customerId),
          branch_id: branchId,
          term_months: String(selectedTerm) as '3' | '6' | '12' | '18' | '24',
          client_card_id: activeCardId ?? undefined,
          items: cart.map(item => ({ product_id: item.product.id, quantity: item.quantity })),
        },
      });
      if (error) throw error;

      setMurabahaCaseId(String(data.data.id));
      toast.success(t('pos_flow.goods_sold'));
    } catch (err: any) {
      setStepError(err.message || t('pos_flow.errors.sell'));
    } finally {
      setLoading(false);
    }
  };

  // ===== 6-QADAM (davomi): "Shartnoma ochish" (POST /murabaha/cases/{case}/contract) → 7-qadam =====
  const handleOpenContract = async () => {
    if (!murabahaCaseId || !selectedTerm) return;
    setLoading(true);
    setStepError(null);
    try {
      const { data, error } = await apiClient.POST('/murabaha/cases/{case}/contract', {
        params: { path: { case: Number(murabahaCaseId) } },
        body: { term_months: String(selectedTerm) as '3' | '6' | '12' | '18' | '24' },
      });
      if (error) throw error;

      setContractId(String(data.data.id));
      // Shartnoma ID shartnoma ochilganda darhol keladi
      setContractNumber(data.data.contract_number ?? '');
      setStep('contract');
    } catch (err: any) {
      setStepError(err.message || t('pos_flow.errors.open_contract'));
    } finally {
      setLoading(false);
    }
  };

  const handleSendViaApp = async () => {
    if (!contractId) return;
    setLoading(true);
    setStepError(null);
    setAppTimedOut(false);
    try {
      const { data, error } = await apiClient.POST('/murabaha/contracts/{contract}/send', {
        params: { path: { contract: Number(contractId) } },
        body: { method: 'app' },
      });
      if (error) throw error;
      if (data.data.contract_number) setContractNumber(data.data.contract_number);

      setConfirmationMethod('app');
      setContractConfirmed(false);
      setStep('sent');

      // Loan Manager statusini kuzatamiz; 2 daqiqada tasdiq kelmasa — SMS ga qaytish taklif qilinadi
      stopPolling();
      const startedAt = Date.now();
      pollingRef.current = setInterval(async () => {
        try {
          const { data: status } = await apiClient.GET('/murabaha/contracts/{contract}/status', {
            params: { path: { contract: Number(contractId) } },
          });
          if (status?.data.status === 'signed') {
            stopPolling();
            setContractConfirmed(true);
            return;
          }
        } catch {
          // keyingi so'rovda qayta urinamiz
        }
        if (Date.now() - startedAt >= 120_000) {
          stopPolling();
          setAppTimedOut(true);
        }
      }, 2000);
    } catch (err: any) {
      setStepError(err.message || t('murabaha_flow.contract_confirm.send_failed'));
    } finally {
      setLoading(false);
    }
  };

  /** Ilova tasdig'i kelmasa — SMS usuliga qaytish */
  const handleFallbackToSms = () => {
    stopPolling();
    setAppTimedOut(false);
    setConfirmationMethod(null);
    setContractSmsSent(false);
    setContractSmsCode('');
    setStep('contract');
  };

  const handleSendContractSms = async () => {
    if (!contractId) return;
    setLoading(true);
    setStepError(null);
    try {
      const { data, error } = await apiClient.POST('/murabaha/contracts/{contract}/send', {
        params: { path: { contract: Number(contractId) } },
        body: { method: 'sms' },
      });
      if (error) throw error;
      if (data.data.contract_number) setContractNumber(data.data.contract_number);
      setContractSmsSent(true);
    } catch (err: any) {
      setStepError(err.message || t('pos_flow.errors.sms_send'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyContractSms = async () => {
    if (contractSmsCode.length !== 6) {
      setContractSmsError(t('pos_flow.errors.sms_6'));
      return;
    }
    if (!contractId) return;
    setContractSmsError('');
    setLoading(true);

    try {
      // Yagona chaqiruv — kod tasdiqlanishi bilan shartnoma yopiladi va sotuv (SaleResource) yaratiladi
      const { data, error } = await apiClient.POST('/murabaha/contracts/{contract}/confirm', {
        params: { path: { contract: Number(contractId) } },
        body: { code: contractSmsCode },
      });
      if (error) throw error;

      setSaleResult(data.data);
      setConfirmationMethod('sms');
      setContractConfirmed(true);
      setStep('sent');
    } catch (err: any) {
      setContractSmsError(err.message || t('pos_flow.errors.contract_code'));
    } finally {
      setLoading(false);
    }
  };

  // T228: kassir mijoz tasdiqlashini kutmasdan shartnomani bekor qiladi — limit qaytadi. Ariza (sotuv va Hujjat
  // hisob-fakturasi) qoladi: o'sha arizaga yangi shartnoma ochiladi, faqat shartnoma va SMS holati tozalanadi
  const handleCancelContract = async () => {
    if (!contractId) return;
    setLoading(true);
    setStepError(null);
    try {
      const { error } = await apiClient.POST('/murabaha/contracts/{contract}/cancel', {
        params: { path: { contract: Number(contractId) } },
      });
      if (error) throw error;

      setCancelContractOpen(false);
      setContractId(null);
      setContractNumber('');
      setConfirmationMethod(null);
      setContractSmsSent(false);
      setContractSmsCode('');
      setContractSmsError('');
      toast.success(t('pos_flow.contract_sms.cancelled'));
      setStep('product_info');
    } catch (err: any) {
      setCancelContractOpen(false);
      setStepError(err.message || t('pos_flow.contract_sms.cancel_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    clearCart();
    navigate('/pos');
  };

  // ========== STEP CONFIG ==========
  const getSteps = () => {
    if (customerType === 'new') {
      return [
        { key: 'identify', label: t('pos_flow.steps.document') },
        { key: 'myid', label: 'MyID' },
        { key: 'card_input', label: t('pos_flow.steps.card') },
        { key: 'customer_created', label: t('pos_flow.steps.customer') },
        { key: 'income', label: t('pos_flow.steps.income') },
        { key: 'limit_result', label: t('pos_flow.steps.limit') },
        { key: 'product_info', label: t('pos_flow.steps.sale') },
        { key: 'contract', label: t('pos_flow.steps.contract') },
        { key: 'sent', label: t('pos_flow.steps.handover') },
      ];

    }

    // Kassadan to'g'ridan-to'g'ri kelgan mijoz — qidiruv qadami yo'q, karta → MyID → limit
    if (hasPresetCustomer) {
      return [
        ...(presetPhoneStep ? [{ key: 'customer_created', label: t('pos_flow.steps.customer') }] : []),
        { key: 'card_input', label: t('pos_flow.steps.card') },
        { key: 'myid', label: 'MyID' },
        { key: 'income', label: t('pos_flow.steps.income') },
        { key: 'limit_result', label: t('pos_flow.steps.limit') },
        { key: 'product_info', label: t('pos_flow.steps.sale') },
        { key: 'contract', label: t('pos_flow.steps.contract') },
        { key: 'sent', label: t('pos_flow.steps.handover') },
      ];
    }

    // Joriy mijoz — Client Manager → mijoz → karta → MyID → limit → sotuv → shartnoma → topshirish
    return [
      { key: 'identify', label: t('pos_flow.steps.search') },
      { key: 'customer_created', label: t('pos_flow.steps.customer') },
      { key: 'card_input', label: t('pos_flow.steps.card') },
      { key: 'myid', label: 'MyID' },
      { key: 'income', label: t('pos_flow.steps.income') },
      { key: 'limit_result', label: t('pos_flow.steps.limit') },
      { key: 'product_info', label: t('pos_flow.steps.sale') },
      { key: 'contract', label: t('pos_flow.steps.contract') },
      { key: 'sent', label: t('pos_flow.steps.handover') },
    ];
  };


  const visibleSteps = step === 'choose_type' ? [] : getSteps();
  const currentIdx = visibleSteps.findIndex(s => s.key === step || (step === 'contract_sms' && s.key === 'contract'));

  const identValidation = validatePassportOrPinfl(identValue);
  // MyID qadamidagi hujjat: yozuvdagi PINFL/pasport, bo'lmasa kassir kiritgani — yorliq qiymatning o'ziga qarab tanlanadi
  const myidDocumentValue = existingCustomer?.pinfl || existingCustomer?.passport || identValue;
  const myidDocument = { value: myidDocumentValue, type: validatePassportOrPinfl(myidDocumentValue || '').type };
  const displayName = customerName || (hasPresetCustomer ? murabahaCustomer?.full_name : '') || '';
  const displayPhone = (hasPresetCustomer ? murabahaCustomer?.phone : phone) || existingCustomer?.phone;

  const progressPct = visibleSteps.length > 1
    ? Math.round(((currentIdx + 1) / visibleSteps.length) * 100)
    : 0;
  const currentStepLabel = currentIdx >= 0 ? visibleSteps[currentIdx]?.label : '';

  // Limit muddati — faqat Loan Manager javobidan (5-qadam)
  const limitExpireDate: string | null =
    limitData?.expire_date ?? limitData?.ExpireDate ?? null;
  const limitExpired = (() => {
    if (!limitExpireDate) return false;
    const d = new Date(limitExpireDate);
    return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
  })();
  const cards: { id: number; masked: string; expiry: string | null }[] = existingCustomer?.cards ?? [];
  const activeCardId = selectedCardId !== null && cards.some(c => c.id === selectedCardId) ? selectedCardId : (cards[0]?.id ?? null);
  const displayCardMasked = cards.find(c => c.id === activeCardId)?.masked ?? cardMasked;
  const showCardList = cards.length > 0 && !addingCard && !cardOtpSent;

  const formatDateUz = (value?: string | null) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const cardCls = 'bg-card border border-border rounded-2xl p-6 shadow-sm';
  const labelCls = 'text-sm font-medium text-muted-foreground';
  const inputCls = 'h-14 text-lg';
  const primaryBtn = 'w-full h-14 text-base font-semibold';
  const backBtn = 'w-full h-14 text-base';
  const sectionTitle = 'text-xl font-semibold text-foreground';

  return (
    <POSLayout>
      <div className="max-w-3xl mx-auto px-4 py-8 md:px-8 animate-fade-in">
        {/* ===== Doimiy kontekst paneli ===== */}
        <MurabahaContextBar
          cartTotal={cartTotal}
          itemCount={cart.length}
          customerName={displayName}
          customerPhone={displayPhone}
          selectedTerm={selectedTerm}
          monthlyPayment={selectedInfo?.monthly ?? null}
          limitAmount={limitData ? (selectedInfo?.limit ?? null) : null}
          limitExpireDate={limitData ? limitExpireDate : null}
          cardMasked={displayCardMasked}
        />

        {/* ===== Header ===== */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('pos_pay.murabaha')}</h1>
            <p className="text-base text-muted-foreground mt-1">{t('pos_flow.subtitle')}</p>
          </div>
          {step !== 'choose_type' && step !== 'sent' && (
            <Button variant="ghost" className="h-12 text-base" onClick={() => setExitOpen(true)}>
              {t('pos_flow.exit_btn')}
            </Button>
          )}
        </div>

        {/* ===== Sessiyani tiklash ===== */}
        <AlertDialog open={!!resumeSnapshot} onOpenChange={open => { if (!open) discardSnapshot(); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('pos_flow.resume.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('pos_flow.resume.desc')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={discardSnapshot} className="h-12 text-base">{t('pos_flow.resume.restart')}</AlertDialogCancel>
              <AlertDialogAction onClick={() => applySnapshot(resumeSnapshot)} className="h-12 text-base">
                {t('pos_flow.resume.continue')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ===== Jarayondan chiqish tasdig'i ===== */}
        <AlertDialog open={exitOpen} onOpenChange={setExitOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('pos_flow.exit.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('pos_flow.exit.desc')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-12 text-base">{t('pos_flow.exit.stay')}</AlertDialogCancel>
              <AlertDialogAction
                className="h-12 text-base"
                onClick={() => { sessionStorage.removeItem(sessionKey); navigate('/pos'); }}
              >
                {t('pos_flow.exit.leave')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>


        {/* ===== Progress ===== */}
        {visibleSteps.length > 0 && (
          <div className="mb-8 space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-foreground">
                {t('pos_flow.step_n', { current: Math.max(currentIdx + 1, 1), total: visibleSteps.length })}
              </span>
              <span className="text-muted-foreground">{currentStepLabel}</span>
            </div>
            <Progress value={progressPct} className="h-2.5" />

            {/* Stepper — tabletda va kattaroq ekranlarda */}
            <div className="hidden sm:flex items-start justify-between gap-1 pt-2">
              {visibleSteps.map((s, i) => (
                <div key={s.key} className="flex flex-1 items-start">
                  <div className="flex flex-1 flex-col items-center gap-2">
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-semibold transition-colors ${
                        i < currentIdx
                          ? 'bg-primary text-primary-foreground'
                          : i === currentIdx
                          ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {i < currentIdx ? <CheckCircle size={20} /> : i + 1}
                    </div>
                    <span className={`text-center text-xs leading-tight ${
                      i <= currentIdx ? 'font-semibold text-primary' : 'text-muted-foreground'
                    }`}>
                      {s.label}
                    </span>
                  </div>
                  {i < visibleSteps.length - 1 && (
                    <div className={`mt-5 h-0.5 flex-1 ${i < currentIdx ? 'bg-primary' : 'bg-border'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== Client Manager (FR-106) ===== */}
        {clientManagerError && (step !== 'myid' || pendingStep) && (
          <div className="mb-6">
            <ClientManagerAlert error={clientManagerError} loading={loading} onRetry={handleClientManagerRetry} />
          </div>
        )}

        {/* ===== Qadam ichidagi xatolar ===== */}
        {stepError && (
          <div className="mb-6">
            <FlowErrorPanel
              message={stepError}
              loading={loading}
              onRetry={() => setStepError(null)}
              retryLabel={t('pos_flow.close')}
            />
          </div>
        )}

        {/* ===== Joriy mijoz topilmadi ===== */}
        {lookupNotFound && (
          <div className="mb-6 space-y-4 rounded-2xl border border-warning/30 bg-warning/10 p-6">
            <div className="flex items-center gap-3">
              <AlertTriangle size={22} className="text-warning" />
              <p className="text-lg font-semibold text-foreground">{t('pos_flow.not_found.title')}</p>
            </div>
            <p className="text-base text-muted-foreground">
              {t('pos_flow.not_found.desc')}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Button variant="outline" className={backBtn} onClick={resetIdentify}>
                {t('pos_flow.not_found.check_other')}
              </Button>
              <Button className={primaryBtn} onClick={switchToNewCustomer}>
                <UserPlus size={18} className="mr-2" />
                {t('pos_flow.not_found.as_new')}
              </Button>
            </div>
          </div>
        )}



        {/* ========== STEP 0: Choose customer type ========== */}
        {step === 'choose_type' && (
          <div className="space-y-6">
            <p className={sectionTitle}>{t('pos_flow.choose.title')}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => handleChooseType('new')}
                className="text-left bg-card border-2 border-border rounded-2xl p-6 min-h-[180px] shadow-sm cursor-pointer hover:border-primary hover:shadow-md transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <UserPlus size={28} className="text-primary" />
                </div>
                <p className="text-xl font-semibold text-foreground">{t('pos_flow.choose.new')}</p>
                <p className="text-sm text-muted-foreground mt-2">{t('pos_flow.choose.new_desc')}</p>
              </button>
              <button
                type="button"
                onClick={() => handleChooseType('existing')}
                className="text-left bg-card border-2 border-border rounded-2xl p-6 min-h-[180px] shadow-sm cursor-pointer hover:border-primary hover:shadow-md transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-success/10 flex items-center justify-center mb-4">
                  <UserCheck size={28} className="text-success" />
                </div>
                <p className="text-xl font-semibold text-foreground">{t('pos_flow.choose.existing')}</p>
                <p className="text-sm text-muted-foreground mt-2">{t('pos_flow.choose.existing_desc')}</p>
              </button>
            </div>
            <Button variant="outline" onClick={() => navigate(-1)} className={backBtn}>{t('pos_flow.back')}</Button>
          </div>
        )}

        {/* ========== STEP 1: Identify ========== */}
        {step === 'identify' && (
          <div className="space-y-6">
            <div className={cardCls + ' space-y-5'}>
              <p className={sectionTitle}>
                {customerType === 'new' ? t('pos_flow.identify.title_new') : t('pos_flow.identify.title_existing')}
              </p>




              <div className="space-y-2">
                <label className={labelCls}>{t('pos_flow.identify.doc_label')}</label>
                <Input
                  placeholder={t('pos_flow.identify.doc_placeholder')}
                  value={identValue}
                  onChange={e => {
                    let val = e.target.value.toUpperCase();
                    const raw = val.replace(/\s/g, '');
                    if (/^[A-Z]/.test(raw)) {
                      const letters = raw.replace(/[^A-Z]/g, '').slice(0, 2);
                      const afterLetters = raw.slice(letters.length).replace(/[^0-9]/g, '').slice(0, 7);
                      val = letters.length === 2 && afterLetters.length > 0
                        ? letters + ' ' + afterLetters
                        : letters;
                    } else if (/^\d+$/.test(raw)) {
                      val = raw.slice(0, 14);
                    } else {
                      val = raw.replace(/[^A-Z0-9]/g, '');
                    }
                    setIdentValue(val);
                    setIdentError('');
                  }}
                  className={`${inputCls} tracking-wider font-mono ${identError ? 'border-destructive' : ''}`}
                  autoFocus
                  maxLength={15}
                />
                {identError && <p className="text-sm text-destructive">{identError}</p>}
                {!identError && identValidation.type && (
                  <p className="text-sm text-success">
                    {identValidation.type === 'passport' ? t('pos_flow.identify.passport_ok') : t('pos_flow.identify.pinfl_ok')}
                  </p>
                )}
              </div>

              {customerType === 'new' && (
                <>
                  <div className="space-y-2">
                    <label className={labelCls}>{t('pos_flow.identify.birth_date')}</label>
                    <Input
                      type="date"
                      value={birthDate}
                      onChange={e => { setBirthDate(e.target.value); setIdentError(''); }}
                      className={`${inputCls} font-mono`}
                      disabled={identOtpSent}
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <label className={labelCls}>{t('pos_flow.phone')}</label>
                <Input
                  placeholder="+998901234567"
                  value={phone}
                  onChange={e => {
                    let val = e.target.value.replace(/[^+0-9]/g, '');
                    if (!val.startsWith('+998')) val = '+998';
                    setPhone(val);
                    setPhoneError('');
                  }}
                  className={`${inputCls} font-mono ${phoneError ? 'border-destructive' : ''}`}
                  maxLength={13}
                  inputMode="tel"
                  disabled={identOtpSent}
                />
                {phoneError && <p className="text-sm text-destructive">{phoneError}</p>}
              </div>

              {customerType === 'new' && identOtpSent && (
                <OtpPanel
                  title={t('murabaha_flow.identify_otp.title')}
                  phone={phone}
                  code={identOtpCode}
                  onCodeChange={code => { setIdentOtpCode(code); setIdentOtpError(''); }}
                  onSubmit={handleIdentOtpVerify}
                  onResend={() => sendIdentOtp(true)}
                  onEdit={resetIdentOtp}
                  editLabel={t('murabaha_flow.identify_otp.edit')}
                  error={identOtpError}
                  hint={identOtpHint}
                  loading={loading}
                />
              )}

            </div>

            <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
              <Button variant="outline" onClick={() => { setStep('choose_type'); setCustomerType(null); setLookupNotFound(false); resetIdentOtp(); }} className={backBtn}>{t('pos_flow.back')}</Button>
              {customerType === 'new' && identOtpSent ? (
                <Button onClick={handleIdentOtpVerify} className={primaryBtn} disabled={identOtpCode.length !== 6 || loading}>
                  {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <ShieldCheck size={18} className="mr-2" />}
                  {t('murabaha_flow.identify_otp.verify')}
                </Button>
              ) : (
              <Button
                onClick={handleIdentSubmit}
                className={primaryBtn}
                disabled={!identValidation.valid || phone.length < 13 || loading}
              >
                {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <ArrowRight size={18} className="mr-2" />}
                {customerType === 'existing' ? t('pos_flow.identify.find') : t('pos_flow.identify.send_sms')}
              </Button>
              )}
            </div>

          </div>
        )}

        {/* ========== STEP 2: MyID identifikatsiya ========== */}
        {step === 'myid' && (
          <div className="space-y-6">
            <div className={cardCls + ' space-y-5'}>
              <div className="flex flex-wrap items-center gap-3">
                <ScanFace size={22} className="text-primary" />
                <p className={sectionTitle}>{t('murabaha_flow.myid.title')}</p>
                {myidMock && (
                  <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/20">MOCK</Badge>
                )}
              </div>
              <p className="text-base text-muted-foreground">
                {customerType === 'existing' ? t('murabaha_flow.myid.hint_existing') : t('murabaha_flow.myid.hint_new')}
              </p>
              <div className="grid gap-2 sm:grid-cols-2 text-base text-muted-foreground">
                <p>{myidDocument.type === 'pinfl' ? 'PINFL' : t('murabaha_flow.myid.passport')}: <span className="font-mono text-foreground">{myidDocument.value || '—'}</span></p>
                <p>{t('murabaha_flow.myid.birth_date')}: <span className="font-mono text-foreground">{birthDate || existingCustomer?.birth_date || '—'}</span></p>
                <p>{t('murabaha_flow.myid.phone')}: <span className="font-mono text-foreground">{phone}</span></p>
              </div>
              {customerType === 'existing' && !existingCustomer?.birth_date && (
                <div className="space-y-2">
                  <label className={labelCls}>{t('murabaha_flow.myid.birth_date')}</label>
                  <Input
                    type="date"
                    value={birthDate}
                    onChange={e => { setBirthDate(e.target.value); setStepError(null); }}
                    className={`${inputCls} font-mono`}
                  />
                </div>
              )}
              <MyIdWidget
                onStart={handleMyIdStart}
                onPassed={handleMyIdPassed}
                onExit={handleMyIdExit}
                onContinue={() => setStep(customerType === 'existing' ? 'income' : 'card_input')}
                verified={!!myidData}
                mock={myidMock}
                disabled={loading}
              />
            </div>

            <div className="grid gap-3">
              <Button variant="outline" onClick={() => setStep(customerType === 'existing' ? 'card_input' : 'identify')} className={backBtn} disabled={loading}>{t('murabaha_flow.myid.back')}</Button>
            </div>
          </div>
        )}

        {/* ========== STEP 3: Karta (Card Manager + SMS) ========== */}
        {step === 'card_input' && (
          <div className="space-y-6">
            <div className={cardCls + ' space-y-5'}>
              <div className="flex flex-wrap items-center gap-3">
                <CreditCard size={22} className="text-primary" />
                <p className={sectionTitle}>{t('pos_flow.card.title')}</p>
                {isMockConnector('CARD_MANAGER') && (
                  <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/20">MOCK</Badge>
                )}

              </div>

              {showCardList && (
                <div className="space-y-3">
                  <p className="flex items-center gap-2 text-success font-medium"><CheckCircle size={18} />{t('pos_flow.card.linked_title')}</p>
                  {cards.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCardId(c.id)}
                      className={`w-full flex items-center justify-between rounded-xl border-2 p-4 text-left transition-all ${c.id === activeCardId ? 'border-primary bg-primary/5' : 'border-border'}`}
                    >
                      <span className="font-mono text-lg tracking-wider">{c.masked}</span>
                      {c.expiry && <span className="text-sm text-muted-foreground">{t('pos_flow.card.expires', { date: c.expiry })}</span>}
                    </button>
                  ))}
                </div>
              )}

              {!showCardList && (<>
              <div className="space-y-2">
                <label className={labelCls}>{t('pos_flow.card.number_label')}</label>
                <Input
                  placeholder="9860 1524 3444 3425"
                  value={cardNumber}
                  onChange={e => {
                    setCardNumber(formatCardNumber(e.target.value));
                    setCardError('');
                  }}
                  className={`${inputCls} tracking-wider font-mono`}
                  maxLength={19}
                  autoFocus
                  disabled={cardOtpSent}
                />
                {cardNumber.replace(/\s/g, '').length > 0 && cardNumber.replace(/\s/g, '').length < 16 && (
                  <p className="text-sm text-warning">{t('pos_flow.card.digits_progress', { n: cardNumber.replace(/\s/g, '').length })}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className={labelCls}>{t('pos_flow.card.expiry_label')}</label>
                <Input
                  placeholder="12/32"
                  value={cardExpiry}
                  onChange={e => {
                    setCardExpiry(formatExpiry(e.target.value));
                    setCardError('');
                  }}
                  className={`${inputCls} font-mono w-40`}
                  maxLength={5}
                  disabled={cardOtpSent}
                />
              </div>

              {cardError && <p className="text-sm text-destructive">{cardError}</p>}

              {addingCard && !cardOtpSent && cards.length > 0 && (
                <Button variant="ghost" onClick={() => { setAddingCard(false); setCardError(''); }}>{t('pos_flow.back')}</Button>
              )}

              {cardOtpSent && cardOwnCode && <p className="text-sm text-muted-foreground">{t('pos_flow.card.own_otp_note')}</p>}

              {cardOtpSent && (
                <OtpPanel
                  title={t('pos_flow.card.otp_title')}
                  phone={cardPhoneMask ?? phone}
                  code={smsCode}
                  onCodeChange={code => { setSmsCode(code); setSmsError(''); }}
                  onSubmit={handleCardOtpVerify}
                  onResend={handleCardResend}
                  onEdit={resetCardOtp}
                  editLabel={t('pos_flow.card.otp_edit')}
                  error={smsError}
                  hint={cardOtpHint}
                  loading={loading}
                  expiresAt={cardExpiresAt}
                />
              )}
              </>)}
            </div>

            <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
              <Button variant="outline" onClick={() => setStep(customerType === 'existing' ? (hasPresetCustomer ? 'choose_type' : 'customer_created') : 'myid')} className={backBtn} disabled={hasPresetCustomer}>{t('pos_flow.back')}</Button>
              {showCardList ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Button variant="outline" onClick={() => setAddingCard(true)} className={primaryBtn}>{t('pos_flow.card.add_another')}</Button>
                  <Button onClick={proceedAfterCard} className={primaryBtn}>{t('pos_flow.card.continue')} <ArrowRight size={18} className="ml-2" /></Button>
                </div>
              ) : cardOtpSent ? (
                <Button onClick={handleCardOtpVerify} className={primaryBtn} disabled={smsCode.length !== 6 || loading}>
                  {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <ShieldCheck size={18} className="mr-2" />}
                  {t('pos_flow.confirm')}
                </Button>
              ) : (
                <Button
                  onClick={handleCardSubmit}
                  className={primaryBtn}
                  disabled={cardNumber.replace(/\s/g, '').length !== 16 || cardExpiry.length !== 5 || loading}
                >
                  {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
                  {t('pos_flow.card.add')} <ArrowRight size={18} className="ml-2" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ========== Mijoz ========== */}
        {step === 'customer_created' && (
          <div className="space-y-6">
            <div className={cardCls + ' space-y-4'}>
              <div className="flex flex-wrap items-center gap-3">
                <CheckCircle size={24} className="text-success" />
                <p className={sectionTitle}>
                  {customerType === 'existing' ? t('pos_flow.customer.found') : t('pos_flow.customer.card_confirmed')}
                </p>
                
              </div>
              <div className="grid gap-3 sm:grid-cols-2 text-base text-muted-foreground">
                <p>{t('pos_flow.customer.name')}: <span className="text-foreground font-medium">{displayName || '—'}</span></p>
                <p>{identValidation.type === 'pinfl' ? 'PINFL' : t('pos_flow.passport')}: <span className="text-foreground font-mono">{existingCustomer?.pinfl || existingCustomer?.passport || identValue}</span></p>
                <p>{t('pos_flow.birth_date')}: <span className="text-foreground font-mono">{birthDate || existingCustomer?.birth_date || '—'}</span></p>
                <p>{t('pos_flow.phone_short')}: <span className="text-foreground">{phone}</span></p>
                {customerType !== 'existing' && (
                  <>
                    <p>{t('pos_flow.card.label')}: <span className="text-foreground font-mono">{cardMasked || maskCardNumber(cardNumber)}</span></p>
                    <p>{t('pos_flow.card.expiry_short')}: <span className="text-foreground font-mono">{cardExpiry}</span></p>
                  </>
                )}
                <p>Client ID: <span className="text-foreground font-mono">{clientId || '—'}</span></p>
                {myidData?.comparison_value != null && (
                  <p>{t('pos_flow.customer.myid_match')}: <span className="text-foreground font-mono">{(myidData.comparison_value * 100).toFixed(1)}%</span></p>
                )}
              </div>
              {customerType === 'existing' && (
                <p className="text-sm text-muted-foreground">
                  {t('pos_flow.customer.next_step')}
                </p>
              )}
              {phoneUnverified && (
                <div className="space-y-3 rounded-lg border border-warning/30 bg-warning/5 p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={18} className="text-warning" />
                    <p className="font-medium">{t('murabaha_flow.phone_verification.title')}</p>
                  </div>
                  <p className="text-sm text-muted-foreground">{t('murabaha_flow.phone_verification.hint', { phone })}</p>
                  {phoneOtpSent ? (
                    <OtpPanel
                      title={t('murabaha_flow.identify_otp.title')}
                      phone={phone}
                      code={phoneOtpCode}
                      onCodeChange={code => { setPhoneOtpCode(code); setPhoneOtpError(''); }}
                      onSubmit={handlePhoneOtpVerify}
                      onResend={() => sendPhoneOtp(true)}
                      error={phoneOtpError}
                      hint={phoneOtpHint}
                      loading={loading}
                    />
                  ) : (
                    <>
                      {phoneOtpError && <p className="text-sm text-destructive">{phoneOtpError}</p>}
                      <Button onClick={() => sendPhoneOtp(false)} variant="outline" disabled={!PHONE_REGEX.test(phone) || loading}>
                        {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <ShieldCheck size={18} className="mr-2" />}
                        {t('murabaha_flow.phone_verification.send')}
                      </Button>
                      {!PHONE_REGEX.test(phone) && <p className="text-sm text-destructive">{t('murabaha_flow.phone_verification.bad_phone')}</p>}
                    </>
                  )}
                </div>
              )}
            </div>
            {customerType === 'existing' ? (
              <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
                <Button variant="outline" onClick={resetIdentify} className={backBtn}>{t('pos_flow.customer.other')}</Button>
                <Button onClick={() => setStep('card_input')} className={primaryBtn} disabled={phoneUnverified}>
                  {t('pos_flow.card.add')} <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>
            ) : (
              <Button onClick={() => setStep('income')} className={primaryBtn}>
                {t('pos_flow.customer.enter_income')} <ArrowRight size={18} className="ml-2" />
              </Button>
            )}
          </div>
        )}

        {/* ========== DAROMAD (Limitdan oldin) ========== */}
        {step === 'income' && (
          <div className="space-y-6">
            <div className={cardCls + ' space-y-5'}>
              <div className="flex flex-wrap items-center gap-3">
                <HandCoins size={22} className="text-primary" />
                <p className={sectionTitle}>{t('pos_flow.income.title')}</p>
              </div>
              <p className="text-base text-muted-foreground">
                {t('pos_flow.income.desc')}
              </p>

              <IncomeStep rows={incomeRows} onChange={(r) => { setIncomeRows(r); setIncomeError(''); }} />

              {!!incomeError && <p className="text-sm text-destructive">{incomeError}</p>}
            </div>

            {stepError && <FlowErrorPanel message={stepError} onRetry={handleSubmitIncome} />}

            <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
              <Button
                variant="outline"
                className={backBtn}
                onClick={() => setStep(customerType === 'existing' ? 'myid' : 'customer_created')}
                disabled={limitLoading}
              >
                {t('pos_flow.back')}
              </Button>
              <Button onClick={handleSubmitIncome} className={primaryBtn} disabled={limitLoading}>
                {limitLoading ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
                {t('pos_flow.income.get_limit')} <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </div>
        )}



        {/* ========== NO LIMIT ========== */}
        {step === 'no_limit' && (
          <div className={cardCls + ' space-y-5 text-center'}>
            <div className="w-20 h-20 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto">
              <AlertTriangle size={38} />
            </div>
            <p className="text-2xl font-semibold text-foreground">{t('pos_flow.no_limit.title')}</p>
            <p className="text-base text-muted-foreground">
              {limitData?.error_code === 'NO_INCOME_DATA'
                ? t('loan_limit.no_income_data')
                : limitData?.status === 'rejected'
                  ? t('loan_limit.rejected')
                  : t('pos_flow.no_limit.desc')}
            </p>
            {!!limitData?.violation_codes?.length && (
              <p className="text-xs text-muted-foreground font-mono">{t('loan_limit.reason_codes', { codes: limitData.violation_codes.join(', ') })}</p>
            )}
            <div className="rounded-xl bg-muted/50 p-4 text-left text-sm text-muted-foreground space-y-1">
              <p>{t('pos_flow.cart_total')}: <span className="font-mono text-foreground">{formatUZS(cartTotal)}</span></p>
              {!!maxLimit && <p>{t('pos_flow.max_limit')}: <span className="font-mono text-foreground">{formatUZS(maxLimit)}</span></p>}
              <p>{t('pos_flow.no_limit.solution')}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3 pt-2">
              <Button variant="outline" onClick={() => navigate('/pos')} className={backBtn}>{t('pos_flow.edit_cart')}</Button>
              <Button variant="outline" onClick={() => customerId && runLimitCheck(customerId)} className={backBtn} disabled={!customerId || limitLoading}>
                {limitLoading ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
                {t('pos_flow.requery_limit')}
              </Button>
              <Button variant="outline" onClick={resetIdentify} className={backBtn}>{t('pos_flow.customer.other')}</Button>
            </div>
          </div>
        )}

        {/* ========== STEP 5: Limit Result ========== */}
        {step === 'limit_result' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <CheckCircle size={22} className="text-success" />
              <p className={sectionTitle}>
                {hasPresetCustomer ? t('pos_flow.limit.ready') : customerType === 'existing' ? t('pos_flow.limit.found_available') : t('pos_flow.limit.offer')}
              </p>
              {isMockConnector('LOAN_MANAGER') && (
                <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/20">MOCK</Badge>
              )}
            </div>

            {(customerType === 'existing' || hasPresetCustomer) && (
              <div className={cardCls + ' space-y-4'}>
                <div className="grid gap-2 sm:grid-cols-2 text-base text-muted-foreground">
                  <p>{t('pos_flow.customer_label')}: <span className="text-foreground font-medium">{displayName || '—'}</span></p>
                  <p>{t('pos_flow.phone_short')}: <span className="text-foreground font-mono">{displayPhone || '—'}</span></p>
                  {identValue && (
                    <p>{identValidation.type === 'pinfl' ? 'PINFL' : t('pos_flow.passport')}: <span className="text-foreground font-mono">{identValue}</span></p>
                  )}
                  {(birthDate || existingCustomer?.birth_date) && (
                    <p>{t('pos_flow.birth_date')}: <span className="text-foreground font-mono">{formatDateUz(birthDate || existingCustomer?.birth_date)}</span></p>
                  )}
                  {!!displayCardMasked && (
                    <p>{t('pos_flow.card.label')}: <span className="text-foreground font-mono">{displayCardMasked}</span></p>
                  )}
                  <p>
                    MyID:{' '}
                    {(existingCustomer?.myid_verified_at || myidData) ? (
                      <span className="text-success font-medium">{t('pos_flow.myid_verified')}</span>
                    ) : (
                      <span className="text-warning font-medium">{t('pos_flow.myid_unverified')}</span>
                    )}
                  </p>
                </div>
                {limitExpireDate && (
                  <div className={`rounded-xl p-3 text-sm ${limitExpired ? 'bg-destructive/10 text-destructive' : 'bg-muted/50 text-muted-foreground'}`}>
                    {limitExpired
                      ? t('pos_flow.limit.expired', { date: formatDateUz(limitExpireDate) })
                      : t('pos_flow.limit.valid_until', { date: formatDateUz(limitExpireDate) })}
                    {limitExpired && customerId && (
                      <Button variant="outline" size="sm" className="ml-3" onClick={() => runLimitCheck(customerId)} disabled={limitLoading}>
                        {t('pos_flow.limit.requery_short')}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Limit yuklanmoqda / qayta urinish */}
            {limitLoading && (
              <div className={cardCls + ' flex items-center gap-3 text-base text-muted-foreground'}>
                <Loader2 size={20} className="animate-spin text-primary" />
                <div>
                  <p>{t('loan_limit.scoring')}</p>
                  <p className="text-sm">{t('loan_limit.scoring_hint')}</p>
                </div>
              </div>
            )}
            {!limitLoading && !limitData && (
              <FlowErrorPanel
                message={t('pos_flow.limit.not_received')}
                onRetry={() => (hasPresetCustomer || !customerId ? fetchPresetLimit() : runLimitCheck(customerId))}
                loading={limitLoading}
              />
            )}




            {/* Limitlar */}
            <div className={cardCls}>
              <p className="text-base font-semibold text-foreground mb-4">{t('pos_flow.limit.limits_title')}</p>
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 text-sm text-muted-foreground font-medium">{t('pos_flow.limit.th_term')}</th>
                    <th className="text-right py-3 text-sm text-muted-foreground font-medium">{t('pos_flow.limit.th_limit')}</th>
                    <th className="text-right py-3 text-sm text-muted-foreground font-medium">{t('pos_flow.limit.th_monthly')}</th>
                    <th className="text-right py-3 text-sm text-muted-foreground font-medium">{t('pos_flow.limit.th_status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {termOptions.map((o, idx) => (
                    <tr
                      key={o.tenor}
                      className={`${idx < termOptions.length - 1 ? 'border-b border-border' : ''} ${selectedTerm === o.tenor ? 'bg-primary/5' : ''}`}
                    >
                      <td className="py-3">{t('pos_context.months', { n: o.tenor })}</td>
                      <td className="py-3 text-right font-mono tabular-nums">{formatUZS(o.limit)}</td>
                      <td className="py-3 text-right font-mono tabular-nums">{o.canAfford ? formatUZS(o.monthly) : '—'}</td>
                      <td className="py-3 text-right">
                        {o.canAfford
                          ? <Badge className="bg-success/10 text-success border-0">{t('pos_flow.limit.enough')}</Badge>
                          : <Badge className="bg-destructive/10 text-destructive border-0">{t('pos_flow.limit.not_enough')}</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Savat */}
            <div className={cardCls}>
              <p className="text-base font-semibold text-foreground mb-4">{t('pos_flow.cart.title')}</p>
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 text-sm text-muted-foreground font-medium">{t('pos_flow.cart.th_product')}</th>
                    <th className="text-center py-3 text-sm text-muted-foreground font-medium">{t('pos_flow.cart.th_qty')}</th>
                    <th className="text-right py-3 text-sm text-muted-foreground font-medium">{t('pos_flow.cart.th_sum')}</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((item, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="py-3 text-foreground">{item.product.name}</td>
                      <td className="py-3 text-center">{item.quantity}</td>
                      <td className="py-3 text-right font-mono tabular-nums text-foreground">{formatUZS(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border">
                    <td colSpan={2} className="py-3 font-semibold text-foreground">{t('pos_flow.cart.total_colon')}</td>
                    <td className="py-3 text-right text-lg font-bold font-mono tabular-nums text-foreground">{formatUZS(cartTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Muddat tanlash */}
            <p className={sectionTitle}>{t('pos_flow.term.choose')}</p>

            <div className="grid gap-5 lg:grid-cols-2">
              {affordableTerms.map(o => (
                <div
                  key={o.tenor}
                  onClick={() => handleSelectTerm(o.tenor)}
                  className="bg-card border-2 border-border rounded-2xl p-6 cursor-pointer hover:border-primary hover:shadow-md transition-all space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="text-base px-4 py-1">{t('pos_context.months', { n: o.tenor })}</Badge>
                    <span className="text-base font-semibold text-primary">{t('pos_flow.term.select')}</span>
                  </div>
                  <div className="rounded-xl bg-muted p-4 text-center">
                    <p className="text-sm text-muted-foreground">{t('pos_flow.term.monthly')}</p>
                    <p className="text-3xl font-bold text-primary tabular-nums">{formatUZS(o.monthly)}</p>
                  </div>
                  <table className="w-full text-base">
                    <tbody>
                      <tr className="border-b border-border"><td className="py-2.5 text-muted-foreground">{t('pos_flow.term.goods_price')}</td><td className="py-2.5 text-right font-mono tabular-nums text-foreground">{formatUZS(cartTotal)}</td></tr>
                      <tr className="border-b border-border"><td className="py-2.5 text-muted-foreground">{t('pos_flow.term.markup_pct')}</td><td className="py-2.5 text-right font-mono text-warning">{(o.markup * 100).toFixed(0)}%</td></tr>
                      <tr className="border-b border-border"><td className="py-2.5 text-muted-foreground">{t('pos_flow.term.markup_amount')}</td><td className="py-2.5 text-right font-mono tabular-nums text-warning">{formatUZS(o.markupAmount)}</td></tr>
                      <tr><td className="py-2.5 text-muted-foreground">{t('pos_flow.term.total_pay')}</td><td className="py-2.5 text-right font-semibold font-mono tabular-nums text-foreground">{formatUZS(o.total)}</td></tr>
                    </tbody>
                  </table>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">{t('pos_flow.term.schedule')}</p>
                    <ScheduleTable schedule={o.schedule} />
                  </div>
                </div>
              ))}
            </div>

            {!hasLimit && !!limitData && (
              <div className="space-y-4 rounded-2xl border border-destructive/20 bg-destructive/10 p-6 text-center">
                <AlertTriangle size={28} className="mx-auto text-destructive" />
                <p className="text-lg font-semibold text-destructive">{t('pos_flow.over_limit.title')}</p>
                <p className="text-base text-foreground">
                  {t('pos_flow.over_limit.cart')}: <span className="font-semibold tabular-nums">{formatUZS(cartTotal)}</span>
                  {' · '}{t('pos_flow.max_limit')}:{' '}
                  <span className="font-semibold tabular-nums">
                    {formatUZS(Math.max(0, ...termOptions.map(o => o.limit)))}
                  </span>
                </p>
                <p className="text-base text-muted-foreground">
                  {termOptions.map(o => `${t('pos_context.months', { n: o.tenor })}: ${formatUZS(o.limit)}`).join(' | ')}
                </p>
                <p className="text-base text-muted-foreground">
                  {t('pos_flow.over_limit.hint')}
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Button variant="outline" className={backBtn} onClick={() => navigate('/pos')}>
                    {t('pos_flow.edit_cart')}
                  </Button>
                  <Button variant="outline" className={backBtn} onClick={fetchPresetLimit} disabled={limitLoading}>
                    {t('pos_flow.requery_limit')}
                  </Button>
                </div>
              </div>
            )}

            <Button variant="outline" onClick={() => {
              if (customerType === 'new') setStep('customer_created');
              else { setStep('choose_type'); setCustomerType(null); }
            }} className={backBtn}>{t('pos_flow.back')}</Button>
          </div>
        )}

        {/* ========== STEP 6: Sotuv (invoice) ========== */}
        {step === 'product_info' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <Package size={22} className="text-primary" />
              <p className={sectionTitle}>{t('pos_flow.steps.sale')}</p>
            </div>

            {/* Sotuvchi va Xaridor rekvizitlari */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className={cardCls + ' space-y-2'}>
                <p className="text-base font-semibold text-foreground">{t('pos_flow.product.seller')}</p>
                <div className="space-y-1 text-base text-muted-foreground">
                  <p className="text-lg font-medium text-foreground">{merchant?.name || '—'}</p>
                  <p>{t('pos_flow.product.legal_name')}: <span className="text-foreground">{merchant?.legal_name || '—'}</span></p>
                  <p>INN: <span className="font-mono text-foreground">{merchant?.inn || '—'}</span></p>
                  <p>{t('pos_flow.product.bank_account')}: <span className="font-mono text-foreground">{merchant?.bank_account || '—'}</span></p>
                  <p>Bank: <span className="text-foreground">{merchant?.bank_name || '—'}</span></p>
                  <p>Bank MFO: <span className="font-mono text-foreground">{merchant?.bank_code || '—'}</span></p>
                  <p>{t('pos_flow.product.address')}: <span className="text-foreground">{merchant?.address || '—'}</span></p>
                  <p>{t('pos_flow.product.status')}: <span className="text-foreground">{merchant?.platform_status || '—'}</span></p>
                </div>
              </div>

              <div className={cardCls + ' space-y-2'}>
                <p className="text-base font-semibold text-foreground">{t('pos_flow.product.buyer')}</p>
                <div className="space-y-1 text-base text-muted-foreground">
                  <p className="text-lg font-medium text-foreground">{hisobRequisites?.name || 'Hisob MMT'}</p>
                  <p>{t('pos_flow.product.legal_name')}: <span className="text-foreground">{hisobRequisites?.legal_name || '—'}</span></p>
                  <p>INN: <span className="font-mono text-foreground">{hisobRequisites?.inn || '—'}</span></p>
                  <p>{t('pos_flow.product.bank_account')}: <span className="font-mono text-foreground">{hisobRequisites?.bank_account || '—'}</span></p>
                  <p>Bank: <span className="text-foreground">{hisobRequisites?.bank_name || '—'}</span></p>
                  <p>Bank MFO: <span className="font-mono text-foreground">{hisobRequisites?.bank_code || '—'}</span></p>
                  <p>{t('pos_flow.product.address')}: <span className="text-foreground">{hisobRequisites?.address || '—'}</span></p>
                </div>
              </div>
            </div>

            {/* Buyurtmachi */}
            <div className={cardCls}>
              <p className="text-base font-semibold text-foreground mb-3">{t('pos_flow.product.orderer')}</p>
              <div className="grid gap-2 sm:grid-cols-2 text-base text-muted-foreground">
                <p>{t('pos_flow.product.full_name')}: <span className="text-foreground font-medium">{displayName}</span></p>
                <p>{t('pos_flow.phone_short')}: <span className="text-foreground font-mono">{displayPhone}</span></p>
                {identValue && (
                  <p>{identValidation.type === 'pinfl' ? 'PINFL' : t('pos_flow.passport')}: <span className="text-foreground font-mono">{identValue}</span></p>
                )}
                {clientId && <p>Client ID: <span className="text-foreground font-mono">{clientId}</span></p>}
              </div>
            </div>

            {/* Tovar — to'liq ma'lumot (yig'ilishi mumkin) */}
            <div className="rounded-2xl border border-border bg-card shadow-sm">
              <button
                type="button"
                onClick={() => setItemsOpen(v => !v)}
                className="flex w-full items-center justify-between gap-3 p-5 text-left"
              >
                <span className="text-base font-semibold text-foreground">
                  {t('pos_flow.product.items_toggle', { n: invoiceItems.length, amount: formatUZS(cartTotal) })}
                </span>
                {itemsOpen ? <ChevronUp size={20} className="text-muted-foreground" /> : <ChevronDown size={20} className="text-muted-foreground" />}
              </button>
              {itemsOpen && (
                <div className="divide-y divide-border border-t border-border">
                  {invoiceItems.map((item, i) => (
                    <div key={i} className="space-y-2 p-5">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-lg font-semibold text-foreground">{item.product_name}</p>
                        <Badge variant="outline" className="text-sm">{item.quantity} {item.unit}</Badge>
                      </div>
                      <div className="grid gap-x-6 gap-y-1 text-sm text-muted-foreground sm:grid-cols-2">
                        <p>SKU: <span className="font-mono text-foreground">{item.sku}</span></p>
                        <p>{t('pos_flow.product.barcode')}: <span className="font-mono text-foreground">{item.barcode || '—'}</span></p>
                        <p>{t('pos_flow.product.brand')}: <span className="text-foreground">{item.brend_nomi || '—'}</span></p>
                        <p>{t('pos_flow.product.attribute')}: <span className="text-foreground">{item.atribut_nomi || '—'}</span></p>
                        <p>MHIK: <span className="font-mono text-foreground">{item.mhik || '—'}</span></p>
                        <p>{t('pos_flow.product.mxik_name')}: <span className="text-foreground">{item.mxik_nomi || '—'}</span></p>
                        <p>{t('pos_flow.product.category')}: <span className="text-foreground">{item.category || '—'}</span></p>
                        <p>{t('pos_flow.product.installment_allowed')}: <span className="text-foreground">{item.is_murabaha_eligible ? t('pos_flow.yes') : t('pos_flow.no')}</span></p>
                        <p>{t('pos_flow.product.unit_price')}: <span className="font-mono text-foreground">{formatUZS(item.unit_price)}</span></p>
                        <p>{t('pos_cart.total')}: <span className="font-mono font-medium text-foreground">{formatUZS(item.total)}</span></p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={cardCls + ' space-y-3'}>
              <div className="flex justify-between text-base">
                <span className="text-muted-foreground">{t('pos_flow.product.total_amount')}</span>
                <span className="font-semibold text-foreground tabular-nums">{formatUZS(cartTotal)}</span>
              </div>
              <div className="flex justify-between text-base">
                <span className="text-muted-foreground">{t('pos_flow.product.term')}</span>
                <span className="text-foreground">{t('pos_context.months', { n: selectedTerm })}</span>
              </div>
              <div className="flex justify-between text-base">
                <span className="text-muted-foreground">{t('pos_flow.product.total_with_markup')}</span>
                <span className="text-foreground font-mono tabular-nums">{formatUZS(selectedInfo?.total ?? 0)}</span>
              </div>
              <div className="flex justify-between text-lg pt-2 border-t border-border">
                <span className="text-muted-foreground">{t('pos_flow.term.monthly')}</span>
                <span className="font-bold text-primary tabular-nums">
                  {formatUZS(selectedInfo?.monthly ?? 0)}
                </span>
              </div>
            </div>

            {murabahaCaseId && (
              <div className="rounded-2xl border border-success/20 bg-success/10 p-5 text-base">
                <p className="font-semibold text-success">{t('pos_flow.goods_sold')}</p>
              </div>
            )}

            {murabahaCaseId && waitingInvoice && (
              <div data-testid="waiting-invoice" className="flex items-start gap-3 rounded-2xl border border-warning/20 bg-warning/10 p-5 text-base">
                <Loader2 size={20} className="mt-0.5 shrink-0 animate-spin text-warning" />
                <div className="space-y-1">
                  <p className="font-semibold text-warning">{t('pos_flow.product.waiting_invoice')}</p>
                  <p className="text-muted-foreground">
                    {t('pos_flow.product.waiting_invoice_hint', {
                      status: caseInvoice
                        ? t(`invoice.status.${caseInvoice.status}`, { defaultValue: caseInvoice.status })
                        : t('pos_flow.product.invoice_missing'),
                    })}
                  </p>
                </div>
              </div>
            )}

            {/* Doimiy (sticky) asosiy amal */}
            <div className="sticky bottom-0 -mx-4 grid gap-3 border-t border-border bg-background/95 px-4 py-4 backdrop-blur sm:grid-cols-[1fr_2fr] md:-mx-8 md:px-8">
              <Button variant="outline" onClick={() => setStep('limit_result')} className={backBtn}>{t('pos_flow.back')}</Button>
              {murabahaCaseId ? (
                <Button onClick={handleOpenContract} className={primaryBtn} disabled={loading || waitingInvoice}>
                  {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <FileText size={18} className="mr-2" />}
                  {t('pos_flow.product.open_contract')}
                </Button>
              ) : (
                <Button onClick={handleSellGoods} className={primaryBtn} disabled={loading}>
                  {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <FileText size={18} className="mr-2" />}
                  {t('pos_flow.product.sell_goods')}
                </Button>
              )}
            </div>
          </div>
        )}


        {/* ========== STEP 7: Contract ========== */}
        {step === 'contract' && (
          <div className="space-y-6">
            <div className={cardCls + ' space-y-4'}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className={sectionTitle}>{t('pos_flow.contract.draft')}</p>
              </div>
              <div className="rounded-xl bg-muted p-4">
                <p className="text-sm text-muted-foreground">{t('pos_flow.contract.id')}</p>
                {contractNumber ? (
                  <p className="text-xl font-bold font-mono text-foreground">{contractNumber}</p>
                ) : (
                  <p className="text-base text-warning">{t('pos_flow.contract.id_pending')}</p>
                )}
              </div>


              <div className="grid gap-2 sm:grid-cols-2 text-base text-muted-foreground">
                <p>{t('pos_flow.customer_label')}: <span className="text-foreground font-medium">{displayName}</span></p>
                {identValue && <p>{identValidation.type === 'pinfl' ? 'PINFL' : t('pos_flow.passport')}: <span className="text-foreground font-mono">{identValue}</span></p>}
                <p>{t('pos_flow.phone_short')}: <span className="text-foreground">{displayPhone}</span></p>
                {(cardNumber || existingCustomer?.card_masked) && (
                  <p>{t('pos_flow.card.label')}: <span className="text-foreground font-mono">{cardNumber ? maskCardNumber(cardNumber) : existingCustomer?.card_masked}</span></p>
                )}
                <p>{t('pos_flow.contract.seller_rep')}: <span className="text-foreground font-medium">{user?.full_name || '—'}</span></p>
                <p>{t('pos_flow.contract.items_count')}: <span className="text-foreground">{t('pos_common.count_pcs', { n: cart.length })}</span></p>
                <p>{t('pos_flow.contract.amount')}: <span className="text-foreground">{formatUZS(cartTotal)}</span></p>
                <p>{t('pos_flow.contract.with_markup')}: <span className="text-foreground font-medium">
                  {formatUZS(selectedInfo?.total ?? 0)}
                </span></p>
                <p>{t('pos_flow.product.term')}: <span className="text-foreground">{t('pos_context.months', { n: selectedTerm })}</span></p>
                <p>{t('pos_flow.contract.monthly')}: <span className="text-foreground font-medium">
                  {formatUZS(selectedInfo?.monthly ?? 0)}
                </span></p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Button onClick={handleSendViaApp} className={primaryBtn} disabled={loading}>
                {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <Send size={18} className="mr-2" />}
                {t('murabaha_flow.contract_confirm.via_app')}
              </Button>
              <Button variant="outline" onClick={() => { setStep('contract_sms'); handleSendContractSms(); }} className={backBtn} disabled={loading}>
                <Phone size={18} className="mr-2" /> {t('murabaha_flow.contract_confirm.via_sms')}
              </Button>
            </div>
            <Button variant="ghost" onClick={() => setStep('product_info')} className={backBtn}>{t('pos_flow.back')}</Button>
          </div>
        )}

        {/* ========== STEP 7b: Contract SMS ========== */}
        {step === 'contract_sms' && (
          <div className="space-y-6">
            <div className={cardCls + ' space-y-4'}>
              <p className={sectionTitle}>{t('pos_flow.contract_sms.title')}</p>
              {contractSmsSent ? (
                <OtpPanel
                  title={t('pos_flow.contract_sms.enter_code')}
                  phone={displayPhone}
                  code={contractSmsCode}
                  onCodeChange={code => { setContractSmsCode(code); setContractSmsError(''); }}
                  onSubmit={handleVerifyContractSms}
                  onResend={handleSendContractSms}
                  error={contractSmsError}
                  hint="111333"
                  loading={loading}
                />
              ) : (
                <div className="flex items-center justify-center py-6">
                  <Loader2 size={22} className="animate-spin text-primary" />
                  <span className="ml-3 text-base text-muted-foreground">{t('pos_flow.contract_sms.sending')}</span>
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
              <Button variant="outline" onClick={() => setStep('contract')} className={backBtn}>{t('pos_flow.back')}</Button>
              {contractSmsSent && (
                <Button onClick={handleVerifyContractSms} className={primaryBtn} disabled={contractSmsCode.length !== 6 || loading}>
                  {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <ShieldCheck size={18} className="mr-2" />}
                  {t('pos_flow.confirm')}
                </Button>
              )}
            </div>
            <Button variant="ghost" onClick={() => setCancelContractOpen(true)} className={backBtn + ' text-destructive hover:text-destructive'} disabled={loading}>
              <XCircle size={18} className="mr-2" /> {t('pos_flow.contract_sms.cancel')}
            </Button>

            <AlertDialog open={cancelContractOpen} onOpenChange={setCancelContractOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('pos_flow.contract_sms.cancel_title')}</AlertDialogTitle>
                  <AlertDialogDescription>{t('pos_flow.contract_sms.cancel_desc')}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="h-12 text-base" disabled={loading}>{t('pos_flow.contract_sms.cancel_keep')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="h-12 text-base"
                    disabled={loading}
                    onClick={(e) => { e.preventDefault(); handleCancelContract(); }}
                  >
                    {loading && <Loader2 size={16} className="animate-spin mr-2" />}
                    {t('pos_flow.contract_sms.cancel_confirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {/* ========== STEP 8: Done ========== */}
        {step === 'sent' && (
          <div className={cardCls + ' text-center space-y-5'}>
            {!contractConfirmed && confirmationMethod === 'app' ? (
              <>
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-warning/10 text-warning">
                  <Clock size={38} />
                </div>
                <p className="text-2xl font-semibold text-foreground">{t('murabaha_flow.contract_confirm.waiting_title')}</p>
                <p className="text-base text-muted-foreground">{t('murabaha_flow.contract_confirm.waiting_text')}</p>
                {!appTimedOut ? (
                  <div className="flex items-center justify-center gap-3 py-2">
                    <Loader2 size={20} className="animate-spin text-primary" />
                    <span className="text-base text-muted-foreground">{t('murabaha_flow.contract_confirm.checking')}</span>
                  </div>
                ) : (
                  <FlowErrorPanel
                    message={t('murabaha_flow.contract_confirm.timed_out')}
                    onRetry={handleFallbackToSms}
                    retryLabel={t('murabaha_flow.contract_confirm.fallback_sms')}
                  />
                )}
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Lock size={14} /> {t('murabaha_flow.contract_confirm.do_not_hand_over')}
                </div>
              </>
            ) : (
              <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-success/10 text-success">
              <CheckCircle size={38} />
            </div>
            <p className="text-2xl font-semibold text-foreground">{t('pos_flow.done.confirmed')}</p>
            <p className="text-base text-muted-foreground">
              {confirmationMethod === 'app' ? t('murabaha_flow.contract_confirm.confirmed_app') : t('murabaha_flow.contract_confirm.confirmed_sms')}
            </p>
            <div className="rounded-xl bg-muted p-4">
              <p className="text-sm text-muted-foreground">{t('pos_flow.contract.id')}</p>
              {contractNumber
                ? <p className="font-mono text-xl font-bold text-foreground">{contractNumber}</p>
                : <p className="text-base text-warning">{t('pos_flow.done.pending')}</p>}
            </div>
            {murabahaCaseId && (
              <div className="text-left">
                <FiscalReceiptPanel caseId={Number(murabahaCaseId)} customerName={displayName} customerPhone={displayPhone} />
              </div>
            )}
            <MurabahaDoneActions
              contractNumber={contractNumber}
              customerName={displayName}
              customerPhone={displayPhone}
              passport={identValidation.type === 'passport' ? identValue : null}
              pinfl={identValidation.type === 'pinfl' ? identValue : null}
              totalAmount={selectedInfo?.total ?? 0}
              monthlyPayment={selectedInfo?.monthly ?? 0}
              termMonths={selectedTerm ?? 0}
              schedule={selectedInfo?.schedule ?? []}
              merchantPayout={cartTotal}
              items={invoiceItems.map(i => ({
                product_name: i.product_name,
                quantity: i.quantity,
                unit_price: i.unit_price,
                total: i.total,
                unit: i.unit,
                mhik: i.mhik,
              }))}
              onFinish={handleFinish}
            />
              </>
            )}
          </div>
        )}
      </div>
    </POSLayout>
  );
}

